import { useNavigate, useParams } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import { useTranslation } from "react-i18next"
import EditCustomInputsForm from "features/storages/categories/customInputs/editCustomInputsForm"
import useCategories from "hooks/useCategories"
import useStorages from "hooks/useStorages"
import useStorageLimits from "hooks/useStorageLimits"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import RightSidebarCategory from "components/sidebar/right/RightSidebarCategory"
import PageTitle from "features/navigation/pageTitle"
import { paramGuardStorage, paramGuardCategory } from "@/utils/paramGuard"
import { FormStatus } from "@/types"
import { useMemo } from "react"
import DataBoxHolder from "@/components/dataBox/dataBoxHolder"
import SmallDataBox from "@/components/dataBox/smallDataBox"

const CustomInputsEdit = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()

    const { targetStorageDocId: storageId } = useTargetStorage()
    const { categoryId } = useParams<{ categoryId: string }>()

    const {
        storage,
        storageStatus
    } = useStorages({ storageId })

    const {
        category,
        categoryStatus
    } = useCategories({ storageId, categoryId })

    // Get storage plan limits
    const storagePlan = storage?.settings?.storagePlan
    const { limits } = useStorageLimits({ storagePlan })

    // Calculate total custom inputs
    const totalCustomInputs = useMemo(() => {
        if (!category?.customInputs?.customInputGroups) return 0
        
        let total = 0
        for (const group of Object.values(category.customInputs.customInputGroups)) {
            total += Object.keys(group.customInputs).length
        }
        return total
    }, [category])

    // Check params and declare safe ones to use in JSX
    if (
        !paramGuardStorage({storageId, storage, storageStatus, navigate}) ||
        !paramGuardCategory({storageId, categoryId, category, categoryStatus, navigate})
    ) {
        return <></>
    }
    const safeStorageId = storageId!;
    const safeCategoryId = categoryId!;

    // Return the JSX.
    return (
        <>
            <PageTitle title={t('titleCustomInputsEdit')} />
            <Section>
                <DataBoxHolder>
                    <SmallDataBox
                        label={t(`labelCustomInputsUsed`)}
                        value={`${totalCustomInputs} / ${limits.customInputsPerCategory}`}
                        colorScheme="gray"
                        />
                </DataBoxHolder>
            </Section>
            <Section>
                <EditCustomInputsForm storageId={safeStorageId} categoryId={safeCategoryId} />
            </Section>
            <RightSidebarCategory categoryId={safeCategoryId} storageId={safeStorageId} />
        </>
    )
}

export default CustomInputsEdit

